import { Tooltip } from 'antd'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { ServerImageInfo } from '@/api/type-server'
import { Trans } from '@/components'
import { ActionMenu, ActionMenuItem } from '@/components/base/ActionMenu'
import { Progress } from '@/components/base/Progress'
import { ImageProvider, ImageStoreStatus } from '@/consts/image'
import { formatBytes, formatDatePreset } from '@/utils/formatter'

import { UploadTask } from '../types'

interface CustomImageTableProps {
  imageList: ServerImageInfo[]
  uploadTasks: UploadTask[]
  onDeleteImage: (imageId: number) => void
  onContinueUpload?: (taskId: string, imageId: number) => void
  onPauseUpload?: (taskId: string) => void
  onRestoreImage?: (image: ServerImageInfo) => void
}

function getErrCodeMessage(t: TFunction, errCode: number): string {
  // Use the central error-code i18n table (src/consts/errorMsg.ts + common.errorCodes).
  return t(`common.errorCodes.${errCode}`, { defaultValue: String(errCode) })
}

function getStatusDisplay(
  t: TFunction,
  storeStatus: number,
  errCode?: number,
  uploadTask?: UploadTask,
  provider?: number,
) {
  // Handle upload task states first
  if (uploadTask) {
    switch (uploadTask.status) {
      case 'starting':
        return { text: t('customImage.status.starting'), color: 'text-tertiary' }
      case 'uploading': {
        const speedText = uploadTask.speed ? formatBytes(uploadTask.speed) + '/s' : '-- KB/s'
        return {
          text: (
            <span className="inline-flex items-center gap-2">
              {speedText && <span>{speedText}</span>}
              <span>{t('customImage.status.uploading', { progress: uploadTask.progress })}</span>
            </span>
          ),
          color: 'text-tertiary',
        }
      }
      case 'pausing':
        return {
          text: t('customImage.status.pausing', { progress: uploadTask.progress }),
          color: 'text-tertiary',
        }
      case 'paused':
        return {
          text: t('customImage.status.paused', { progress: uploadTask.progress }),
          color: 'text-tertiary',
        }
    }
  }

  // Handle server store status
  switch (storeStatus) {
    case ImageStoreStatus.Pending:
      // Pending images coming from a registry URL are still being downloaded by the backend.
      if (provider === ImageProvider.RegistryURL) {
        return { text: t('customImage.status.importing'), color: 'text-tertiary' }
      }
      return { text: t('customImage.status.uploadRequired'), color: 'text-warning' }
    case ImageStoreStatus.Uploaded:
      return { text: t('customImage.status.formatting'), color: 'text-tertiary' }
    case ImageStoreStatus.Formatted:
    case ImageStoreStatus.Finished:
      return { text: t('customImage.status.available'), color: 'text-active' }
    case ImageStoreStatus.Error:
      return {
        text: errCode
          ? t('customImage.status.errorWithCode', { message: getErrCodeMessage(t, errCode) })
          : t('customImage.status.error'),
        color: 'text-error',
      }
    case ImageStoreStatus.Resumable:
      return { text: t('customImage.status.restorable'), color: 'text-primary' }
    default:
      return { text: t('customImage.status.invalid'), color: 'text-error' }
  }
}

export function CustomImageTable({
  imageList,
  uploadTasks,
  onDeleteImage,
  onContinueUpload,
  onPauseUpload,
  onRestoreImage,
}: CustomImageTableProps) {
  const { t } = useTranslation()
  return (
    <table className="table-card mt-4 table-fixed">
      <thead className="bg-panel-gray-100 rounded-lg">
        <tr>
          <th colSpan={2} className="p-4 text-secondary">
            <Trans i18nKey="customImage.tableHeaderID">ID</Trans>
          </th>
          <th colSpan={2} className="p-4 text-secondary">
            <Trans i18nKey="customImage.tableHeaderName">Name</Trans>
          </th>
          <th colSpan={3} className="p-4 text-secondary">
            <Trans i18nKey="customImage.tableHeaderDescription">Description</Trans>
          </th>
          <th colSpan={2} className="p-4 text-secondary">
            <Trans i18nKey="customImage.tableHeaderSize">Size</Trans>
          </th>
          <th colSpan={2} className="p-4 text-secondary">
            <Trans i18nKey="customImage.tableHeaderStatus">Status</Trans>
          </th>
          <th colSpan={2} className="p-4 text-secondary">
            <Trans i18nKey="customImage.tableHeaderCreateTime">Create Time</Trans>
          </th>
          <th colSpan={1} className="p-4 text-secondary">
            <Trans i18nKey="customImage.tableHeaderAction">Action</Trans>
          </th>
        </tr>
      </thead>
      <tbody>
        {imageList.map((image) => (
          <tr key={image.id} className="h-[72px] border-b border-b-neutral-200">
            <td colSpan={2} className="t-sm p-4">
              {image.resID}
            </td>
            <td colSpan={2} className="t-sm p-4 max-w-0">
              <Tooltip title={image.alias} mouseEnterDelay={0}>
                <span className="block truncate">{image.alias}</span>
              </Tooltip>
            </td>
            <td colSpan={3} className="t-sm p-4 max-w-0">
              <Tooltip title={image.description} mouseEnterDelay={0}>
                <span className="block truncate">{image.description || '-'}</span>
              </Tooltip>
            </td>
            <td colSpan={2} className="t-sm p-4">
              {formatBytes(image.size) || '-'}
            </td>
            <td colSpan={2} className="t-sm p-4 align-top">
              {(() => {
                const task = uploadTasks.find((ut) => ut.imageId === image.id)
                const { text, color } = getStatusDisplay(
                  t,
                  image.storeStatus,
                  image.errCode,
                  task,
                  image.provider,
                )
                const showProgress =
                  task && ['starting', 'uploading', 'pausing', 'paused'].includes(task.status)

                return (
                  <div>
                    <span className={`${color} whitespace-normal break-words leading-5`}>
                      {text}
                    </span>
                    {showProgress && (
                      <Progress
                        value={task.progress / 100}
                        color="bg-btn-secondary"
                        className="mt-1 h-1"
                      />
                    )}
                  </div>
                )
              })()}
            </td>
            <td colSpan={2} className="t-sm p-4 text-tertiary">
              {formatDatePreset(image.createdTime * 1000, 'second')}
            </td>
            <td colSpan={1} className="t-sm p-4">
              {(() => {
                const task = uploadTasks.find((ut) => ut.imageId === image.id)
                const isUploading = task && ['starting', 'uploading'].includes(task.status)
                const showContinueUpload = image.storeStatus === 3 && task && onContinueUpload
                const isRestorable = image.storeStatus === ImageStoreStatus.Resumable

                const items: ActionMenuItem[] = isRestorable
                  ? [
                      {
                        label: <Trans i18nKey="customImage.action.restore">Restore</Trans>,
                        onClick: () => onRestoreImage?.(image),
                        show: !!onRestoreImage,
                      },
                      {
                        label: (
                          <Trans i18nKey="customImage.action.permanentDelete">
                            Permanently Delete
                          </Trans>
                        ),
                        onClick: () => onDeleteImage(image.id),
                        type: 'danger',
                      },
                    ]
                  : [
                      {
                        label: <Trans i18nKey="customImage.action.pause">Pause</Trans>,
                        onClick: () => onPauseUpload?.(task!.id),
                        show: !!isUploading && !!onPauseUpload,
                      },
                      {
                        label: <Trans i18nKey="customImage.action.restart">Restart</Trans>,
                        onClick: () => onContinueUpload?.(task!.id, image.id),
                        show: !!showContinueUpload && !isUploading,
                      },
                      {
                        label: <Trans i18nKey="customImage.action.delete">Delete</Trans>,
                        onClick: () => onDeleteImage(image.id),
                        type: 'danger',
                      },
                    ]

                return <ActionMenu items={items} popupContainer="body" />
              })()}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
